import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import axios from "../../../utils/axios";
import CmsfLogo from '../../../reports/ClaimReport/cmsf_logo_update.jpg';


const numberFormat = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

  

const numberToWords = (num) => {
  if (num === null || num === undefined) return "Zero";

  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
    if (n < 100000)
      return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000)
      return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    if (n < 1000000000)
      return (
        convert(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? " " + convert(n % 10000000) : "")
      );
    return n;
  };

  // Split taka and paisa
  const parts = num.toString().split(".");
  const taka = parseInt(parts[0], 10);
  const paisa = parts[1] ? parseInt(parts[1].padEnd(2, "0").slice(0, 2), 10) : 0;

  let words = "";

  if (taka > 0) {
    words += `${convert(taka)} Taka`;
  }

  if (paisa > 0) {
    if (words) words += " and ";
    words += `${convert(paisa)} Paisa`;
  }

  if (!words) return "Zero";

  return words;
};


const toTitleCaseKeepAcronyms = (str) => {
  const acronyms = ["PLC", "LTD", "AB"];
  if (!str) return "-";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => acronyms.includes(word.toUpperCase()) ? word.toUpperCase() :
      word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("-")
    )
    .join(" ");
};

const formatLongDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};


// ======================================================

const SettlementIssuerWiseModal = ({ open, onClose, issuerId, fundTransferRequestId, issuerName }) => {
  const [details, setDetails] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState(""); // For temporary message
  const printRef = useRef();
  const copyRef = useRef();

  // ============= Fetch API When Modal Opens =============
  useEffect(() => {
    if (!open || !issuerId || !fundTransferRequestId) return;

    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await axios.get(
          `/settled-eft-details/issuer-wise-data/${fundTransferRequestId}?issuerid=${issuerId}`
        );

        if (res.data?.success) {
          setDetails(res.data.payload.details || []);
          setSummary(res.data.payload.summary || {});
        } else {
          Swal.fire("No Data", "No issuer wise data found", "warning");
        }
      } catch (err) {
        Swal.fire("Error", "Failed to load issuer wise details", "error");
      }

      setLoading(false);
    };

    fetchData();
  }, [open, issuerId, fundTransferRequestId]);

  // ============= Calculate Total Amount =============
  const totalAmount = details.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  // =================== Download PDF ======================
  const handleDownload = () => {
    const doc = new jsPDF("p", "mm", "a4");

    const clone = printRef.current.cloneNode(true);
    clone.style.fontSize = "13pt";
    clone.style.color = "black";
    clone.style.backgroundColor = "white";

    doc.html(clone, {
      callback: () => doc.save(`Issuer-Wise-Settlement-${issuerName}.pdf`),
      margin: [10, 10, 10, 10],
      autoPaging: "text",
      x: 10,
      y: 10,
      width: 180,
      windowWidth: 1000,
    });
  };
const handleCopy = async () => {
  try {
    const html = copyRef.current.outerHTML;

    await navigator.clipboard.write([
      new window.ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([copyRef.current.innerText], { type: "text/plain" })
      })
    ]);

    // Show temporary message
    setCopyMessage("Copied to clipboard!");
    setTimeout(() => setCopyMessage(""), 3000); // disappears after 3 sec
  } catch (err) {
    console.error("Copy failed", err);
    setCopyMessage("Failed to copy!");
    setTimeout(() => setCopyMessage(""), 3000);
  }
};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Issuer Wise Settlement Details ({issuerName})</DialogTitle>

      <DialogContent>
        {loading ? (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <CircularProgress />
          </div>
        ) : (
          <div ref={printRef} style={{ padding: "10px 10px 10px 10px", fontFamily: "Times New Roman" }}>

            {/* Header Section */}
       {/* ===== Logo + Address Section ===== */}
   <div style={{ textAlign: "center", marginBottom: "30px" }}>
  <img
    src={CmsfLogo}
    alt="CMSF Logo"
    style={{ width: "320px", marginBottom: "0px" }}
  />

  <div style={{ fontSize: "17px", lineHeight: "1.7" }}>
    DCCI Building (Level-3), 65-66 Motijheel C/A, Dhaka-1000<br />
    Tel: +880241052430, Email: claim@cmsfbd.org
  </div>
</div>

{/* ===== Ref No + Date Row ===== */}
<div style={{ display: "flex", justifyContent: "space-between" }}>
  <div>
    <strong>Ref No:</strong> {summary?.refNo}
  </div>

  <div>
    <strong>Date:</strong> {formatLongDate(summary?.noteDate)}
  </div>
</div>




            <br />

            <p>
              <strong>
                Company Secretary <br />
                {summary?.issuerName} <br />
                {summary?.issuerAddress || ""}
              </strong>
            </p>

            <p>
              <strong>Subject: </strong> {summary?.subject}
            </p>


            <div ref={copyRef}>

        <p>Dear Sir,</p>

            <p style={{ textAlign: "justify" }}>
             Please be informed that as per your forwarding letter to the Capital Market
              Stabilization Fund (CMSF) for the settlement of cash dividends to the shareholders
              based on their claims. CMSF has settled the claims after due verification. CMSF
              transferred the cash dividend to the bank accounts of the shareholders through BEFTN
              on  {formatLongDate(summary?.paymentDate)}.
            </p>

            <p>Please find the attached file for details.</p>

            {/* ========== Table Section ========== */}
            <table width="100%" style={{ borderCollapse: "collapse", marginTop: "10px", border: "1px solid black" }}>
              <thead>
                <tr style={{ backgroundColor: "#f2f2f2", textAlign: "center" }}>
                  <th style={{ border: "1px solid black", padding: "5px" }}>SL</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Name of Investor</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>BO/Folio</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Bank</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Branch</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Account No</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Routing No</th>
                  <th style={{ border: "1px solid black", padding: "5px" }}>Amount (BDT)</th>
                </tr>
              </thead>

              <tbody>
                {details.map((item, idx) => (
                  <tr key={item.id}>
                    <td style={{ border: "1px solid black", padding: "5px", textAlign: "center" }}>
                      {idx + 1}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {item.fullName}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {item.boId ?? item.folioNo}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {toTitleCaseKeepAcronyms(item.bankName)}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {toTitleCaseKeepAcronyms(item.branchName)}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {item.accountNo}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px" }}>
                      {item.routingNo}
                    </td>

                    <td style={{ border: "1px solid black", padding: "5px", textAlign: "right" }}>
                      {numberFormat(item.amount)}
                    </td>
                  </tr>
                ))}

                {/* ========== Total Row ========== */}
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "right", border: "1px solid black", padding: "5px" }}
                  >
                    <strong>Total</strong>
                  </td>

                  <td style={{ border: "1px solid black", padding: "5px", textAlign: "right" }}>
                    <strong>{numberFormat(totalAmount)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ========== Amount in Words ========== */}
            <p style={{ marginTop: "15px" }}>
              <strong>Amount in Words (BDT):</strong> {numberToWords(totalAmount)} only.
            </p>
            <br />

            <p>Thank you for your co-operation.</p>

            <p>Sincerely,</p>
            <p>
              <strong>Investor's Claim Settlement Cell<br />Operation Department, CMSF</strong>
            </p>

            </div>

         
          </div>
        )}
      </DialogContent>

      <DialogActions>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {copyMessage && (
              <span style={{ marginRight: "10px", color: "green", fontWeight: "bold" }}>
                {copyMessage}
              </span>
            )}
            <Button variant="outlined" color="primary" onClick={handleCopy}>
              Copy Body
            </Button>

            
          </div>


        <Button onClick={handleDownload} color="primary" variant="contained">
          Download
        </Button>

        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettlementIssuerWiseModal;
